import { portalText, usePortalTranslation, portalLocale } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `outbound` — this agent's campaigns.
 *
 * The campaigns themselves are P14-02's (`routes/campaigns/**`): this tab is the design's
 * index of them, one row per campaign with its progress and what it has cost, each opening
 * the Campaign screen. It reads `listCampaigns(voiceAgentId)` and, for the money on each row,
 * the campaign's own detail — the row view carries counts but not spend, and a cost column
 * computed here from minutes would be a second, unaudited price calculation.
 *
 * The running campaign is drawn in the accent, as the design draws its selected row: it is
 * the one that is calling people right now.
 *
 * AMENDED 2026-09-30 (plan-008 ST-UA, design §22.14, P13): outbound conversations go through the
 * account's own SIP trunk. Beside New campaign, **Ring a number now** opens `RingNowDialog` — one
 * number, rung now, from one of this agent's caller-ID numbers. It is drawn disabled, with the
 * reason beneath the title row, when there is no caller-ID number connected to this agent on a
 * verified trunk, when the language model is resting, or when the balance cannot pay (the account
 * out of credit or stopped at its limit, or this workspace at its monthly budget). A campaign that is
 * due but rings nobody is tagged with why ("No trunk route", "Switched off") instead of "Running".
 * No new `data-screen-label`: the dialog belongs to the Agent screen's Outbound tab.
 *
 * AMENDED 2026-10-09 (design sync): the tab is the strip's "Campaigns" (a survey agent's only). Under
 * the title, links to Contact lists and Do not call. With no phone connection to call from (no
 * caller-ID number of this agent on a verified trunk), both "Ring a number now" and "New campaign"
 * are disabled and the row says "Connect your phone provider first." — the exact gap is its `title`.
 */
import { useQueries, useQuery } from "@tanstack/react-query";
import { CURRENCY_CODE, money, type Money } from "@wetalk/domain";
import { Link } from "@tanstack/react-router";
import { Button, Icon, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useMemberCan } from "../../../shell/member-capability.js";
import { formatMoney } from "../../../money.js";
import { meterStyle } from "../../../meter.js";
import { useGateway } from "../../../api/provider.js";
import { campaignListQuery, campaignQuery, creditBalanceQuery, voiceAgentQuery } from "../../../api/query.js";
import type { CampaignRowView, CampaignStateView } from "../../../api/outbound-view.js";
import type { CreditBalanceView, TestCallAvailabilityView } from "../../../api/gateway.js";
import type { CampaignListRowView } from "../../../api/slice/campaign.js";
import type { SipTrunkView } from "../../../api/slice/sip-trunk.js";
import { callerIdGapOf, diallingStateLabel } from "../../campaigns/campaign-copy.js";
import { sipTrunkListQuery } from "../../numbers/sip-trunk-query.js";
import { TEST_CALL_AVAILABILITY_KEY } from "../../test-console/test-chat-panel.js";
import { RingNowDialog } from "./ring-now-dialog.js";

const STATE_LABEL: Readonly<Record<CampaignStateView, string>> = {
  get draft() { return portalText("operations", "draft_23d33e22"); },
  get scheduled() { return portalText("operations", "scheduled_1cd1bdad"); },
  get running() { return portalText("operations", "running_73989d9c"); },
  get paused() { return portalText("operations", "paused_c7dfb6f1"); },
  get finished() { return portalText("operations", "finished_355bcc57"); },
  // 2026-10-04: a campaign a member ended (`endCampaign`) is stored `cancelled`; the owner's word is "Ended".
  get cancelled() { return portalText("operations", "ended_90303d8d"); },
};

/** A due campaign that rings nobody is tagged with why, not "Running" (plan-008). */
function rowTag(row: CampaignListRowView): { readonly text: string; readonly status: "positive" | "caution" | "muted" } {
  if (row.diallingState === "no_route" || row.diallingState === "switched_off") {
    return { text: diallingStateLabel(row.diallingState), status: "caution" };
  }
  return { text: STATE_LABEL[row.state], status: row.state === "running" ? "positive" : "muted" };
}

/**
 * Why "Ring a number now" cannot be pressed, or `null` when it can: no number to call from, the
 * model resting, or a balance that cannot pay — the three reasons design §22.14 names, in that order.
 * A fact not read yet blocks nothing: the API refuses what the screen could not know, in its words.
 */
export function ringNowUnavailableReason(
  trunk: readonly SipTrunkView[] | undefined,
  voiceAgentId: string,
  availability: TestCallAvailabilityView | undefined,
  balance: CreditBalanceView | undefined,
): string | null {
  if (trunk === undefined) return portalText("operations", "reading_your_sip_trunk_1332067d");
  const gap = callerIdGapOf(trunk, voiceAgentId);
  if (gap !== null) return gap;
  if (availability !== undefined && !availability.modelRunning) {
    return availability.message ?? portalText("operations", "the_language_model_is_resting_it_runs_in_scheduled_windows_76096c8f");
  }
  // AMENDED 2026-10-04: the API's own sentence for what a new conversation would be told now — the
  // same rule and words the dial's 402 carries, so a conversation in progress holding the credit reads
  // as that ("it frees up when that conversation ends"), never as "no credit left".
  if (balance !== undefined && balance.refusal !== null) return balance.refusal.message;
  return null;
}

function progressText(row: CampaignRowView): string {
  if (row.calledCount === 0) return portalText("operations", "value_people_to_call_d790bd12", { v0: String(row.personCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))) });
  const percent = row.personCount === 0 ? 0 : Math.round((row.calledCount / row.personCount) * 100);
  return portalText("operations", "value_of_value_called_value_6f7acd22", { v0: String(row.calledCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))), v1: String(row.personCount.toLocaleString((portalLocale() === "el" ? "el-GR" : "en-GB"))), v2: String(String(percent)) });
}

export function OutboundTab({ voiceAgentId }: { readonly voiceAgentId: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const campaignList = useQuery(campaignListQuery(gateway, voiceAgentId));
  const detail = useQueries({
    queries: (campaignList.data ?? []).map((row) => campaignQuery(gateway, row.campaignId)),
  });
  // plan-008: what "Ring a number now" needs — a number to call from, a running model, a balance.
  const voiceAgent = useQuery(voiceAgentQuery(gateway, voiceAgentId));
  const trunk = useQuery(sipTrunkListQuery(gateway));
  const availability = useQuery({
    queryKey: TEST_CALL_AVAILABILITY_KEY,
    queryFn: () => gateway.readTestCallAvailability(),
    staleTime: 30_000,
  });
  const balance = useQuery(creditBalanceQuery(gateway));
  const [ringOpen, setRingOpen] = useState(false);
  // 2026-10-29: Ring now starts an outbound conversation, so it is offered only to a person the dial routes admit
  // (`CONSOLE_ACT["conversation.dial"]`, the same role list as End call) — never shown to be refused with a 403.
  const mayDial = useMemberCan().can("conversation.dial");
  const ringUnavailable = ringNowUnavailableReason(trunk.data, voiceAgentId, availability.data, balance.data);
  // The design's `pc.notConnected`: nothing to call from yet, so neither calling act is offered.
  const connectionGap = trunk.data === undefined ? null : callerIdGapOf(trunk.data, voiceAgentId);

  if (campaignList.data === undefined) {
    return <p className="c-note">{portalText("operations", "opening_this_agent_s_campaigns_c1e5b008")}</p>;
  }

  const rows = campaignList.data;
  const spentOf = (campaignId: string): Money | null =>
    detail.find((one) => one.data?.campaignId === campaignId)?.data?.cost.spent ?? null;
  const known = rows.map((row) => spentOf(row.campaignId));
  const totalSpent =
    known.length > 0 && known.every((one) => one !== null)
      ? known.reduce<Money>(
          (total, one) => money(total.amountMinor + (one?.amountMinor ?? 0), total.currency),
          money(0, known[0]?.currency ?? CURRENCY_CODE),
        )
      : null;
  const runningCount = rows.filter((row) => row.state === "running").length;
  const highlighted = rows.find((row) => row.state === "running")?.campaignId ?? null;

  return (
    <>
      <div className="c-va-titlerow c-va-titlerow-loose">
        <div>
          <p className="c-va-summary">
            {String(rows.length)} {rows.length === 1 ? portalText("operations", "campaign_23f43ae9") : portalText("operations", "campaigns_8897ac40")} ·{" "}
            {String(runningCount)} {portalText("operations", "running_3c49d9cc")}{totalSpent === null ? null : <> · {formatMoney(totalSpent)} {portalText("operations", "spent_40d77ecb")}</>}
          </p>
          <p className="c-va-subtitle">{portalText("operations", "outbound_campaigns_c5ab2fda")}</p>
          <p className="c-va-outbound-links">
            <Link to="/contact-lists">{portalText("operations", "contact_lists_b2cf5d26")}</Link>
            <Link to="/do-not-call">{portalText("operations", "do_not_call_4e934f47")}</Link>
          </p>
        </div>
        <span className="c-row c-va-outbound-actions">
          {mayDial ? (
            <Button
              variant="secondary"
              disabled={ringUnavailable !== null}
              title={ringUnavailable ?? undefined}
              onClick={() => {
                setRingOpen(true);
              }}
            >
              <Icon glyph={GLYPH.phoneOutgoing} size="md" />
              <span>{portalText("operations", "ring_a_number_now_63f76955")}</span>
            </Button>
          ) : null}
          {connectionGap === null ? (
            <Link to="/campaigns/new" search={{ voiceAgent: voiceAgentId, contactList: "" }} className="btn btn-primary">
              <Icon glyph={GLYPH.plus} size="md" />
              <span>{portalText("operations", "new_campaign_aaf9a8a4")}</span>
            </Link>
          ) : (
            <Button variant="primary" disabled title={connectionGap}>
              <Icon glyph={GLYPH.plus} size="md" />
              <span>{portalText("operations", "new_campaign_aaf9a8a4")}</span>
            </Button>
          )}
          {connectionGap === null ? null : (
            <span className="c-va-outbound-hint" title={connectionGap}>
              {portalText("operations", "connect_your_phone_provider_first_9c289454")}</span>
          )}
        </span>
      </div>
      {!mayDial || ringUnavailable === null || trunk.data === undefined || connectionGap !== null ? null : (
        <p className="c-note rn-unavailable">{ringUnavailable}</p>
      )}
      {mayDial && ringOpen && trunk.data !== undefined ? (
        <RingNowDialog
          voiceAgentId={voiceAgentId}
          voiceAgentName={voiceAgent.data?.displayName ?? "this agent"}
          sipTrunk={trunk.data}
          onClose={() => {
            setRingOpen(false);
          }}
        />
      ) : null}
      <p className="c-va-dek c-va-dek-narrow">
        {portalText("operations", "one_agent_many_campaigns_each_campaign_carries_its_own_que_21d2bf58")}</p>

      {rows.length === 0 ? (
        <p className="c-note">
          {portalText("operations", "this_agent_has_no_campaigns_yet_a_campaign_calls_a_contact_b03706f6")}</p>
      ) : (
        <div className="c-va-campaigns">
          {rows.map((row) => {
            const percent =
              row.personCount === 0 ? 0 : Math.round((row.calledCount / row.personCount) * 100);
            const spent = spentOf(row.campaignId);
            return (
              <Link
                key={row.campaignId}
                to="/campaigns/$campaignId"
                params={{ campaignId: row.campaignId }}
                className="c-va-campaign"
                data-highlight={row.campaignId === highlighted ? "true" : undefined}
              >
                <span className="c-va-campaign-head">
                  <span className="c-va-campaign-name">{row.name}</span>
                  <Tag status={rowTag(row).status}>{rowTag(row).text}</Tag>
                </span>
                <span className="c-va-campaign-progress">{progressText(row)}</span>
                <span
                  className="c-va-campaign-track"
                  role="meter"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                  aria-label={portalText("operations", "value_value_called_7c4d4c81", { v0: String(row.name), v1: String(String(percent)) })}
                >
                  <span className="c-va-campaign-fill" style={meterStyle(percent)} />
                </span>
                <span className="c-va-campaign-foot">
                  <span>{row.startedText}</span>
                  <span>{spent === null ? "" : formatMoney(spent)}</span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
